import { useRef } from 'react';

import { abbreviatedTimezoneToIana } from '_lib/utils/date.utils';

import { toSxArray } from '_lib/utils/helpers';

import { TextFieldProps, useTextFieldProps } from '../TextField/TextField';

import { AdapterDate, DatePickerCommonProps, fromAdapterDate, toAdapterDate } from './datePickerAdapter';

export { usePickerActionsContext } from '@mui/x-date-pickers/hooks';

export type PickerProps = {
  /**
   * Adds an action button inside of the input to clear the value.
   * Note: this also makes the input editable
   * @default false
   */
  clearable?: boolean;
  value: DatePickerCommonProps['value'];
  textFieldProps?: TextFieldProps;
};

export const usePickerProps = (props: PickerProps) => {
  const { sx, ...slotPropsTextField } = useTextFieldProps({ fullWidth: true, ...props.textFieldProps });

  // Reference for picker button in picker components. SHOULD be passed along to the rendered text box
  // so any click on the text box element can be forwarded to the date picker button.
  const buttonRef: React.ForwardedRef<HTMLButtonElement> = useRef(null);
  return {
    slotPropsField: { readOnly: !props.clearable || !props.value, clearable: props.clearable },
    slotPropsOpenPickerButtonRef: buttonRef,
    slotPropsTextFieldOverrides: {
      onClick: () => {
        // NOTE: we intentionally focus our button so that the input is not the last focused element.
        // this ensures the input will not regain focus after the date is selected
        buttonRef?.current?.focus();
        buttonRef?.current?.click();
      },
      sx: [
        ...toSxArray(sx),
        props.clearable && {
          '& .MuiButtonBase-root.clearButton': {
            // By default, MUI hides the clear button when picker is not focused.
            // This will ensure it is always visible
            opacity: 1
          }
        }
      ],
      ...slotPropsTextField
    }
  };
};

type BaseAdapterProps = Pick<DatePickerCommonProps, 'value' | 'timezone' | 'onChange'>;

export const useBaseAdapterProps = (props: BaseAdapterProps) => ({
  value: props.value ? toAdapterDate(props.value) : null,
  timezone: props.timezone && abbreviatedTimezoneToIana(props.timezone),
  onChange: (internalValue: AdapterDate | null) => {
    if (props.onChange) props.onChange(internalValue ? fromAdapterDate(internalValue) : null);
  }
});

type MonthAdapterProps = Pick<DatePickerCommonProps, 'onMonthChange'>;

export const useMonthAdapterProps = (props: MonthAdapterProps) => ({
  onMonthChange: (internalValue: AdapterDate | null) => {
    if (props.onMonthChange) props.onMonthChange(internalValue ? fromAdapterDate(internalValue) : null);
  }
});

type DateBoundProps = Pick<DatePickerCommonProps, 'minDate' | 'maxDate' | 'timezone'>;

// NOTE - `minDate` and `maxDate` do not appear to respect the timezone we pass in (`value` does)
export const useDateBoundProps = (props: DateBoundProps) => ({
  minDate: props.minDate ? toAdapterDate(props.minDate, props.timezone) : undefined,
  maxDate: props.maxDate ? toAdapterDate(props.maxDate, props.timezone) : undefined
});

type TimeAdapterProps = Pick<DatePickerCommonProps, 'minTime' | 'maxTime' | 'timezone'>;

// NOTE - `minTime` and `maxTime` do not appear to respect the timezone we pass in (`value` does)
export const useTimeBoundProps = (props: TimeAdapterProps) => ({
  minTime: props.minTime ? toAdapterDate(props.minTime, props.timezone) : undefined,
  maxTime: props.maxTime ? toAdapterDate(props.maxTime, props.timezone) : undefined
});
